import { useRef, createContext, useContext, useCallback, useSyncExternalStore } from "react";
import { setNestedValue, getNestedValue } from "../utils/objects/nestedValue";

function shallowEqual(a, b) {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || a === null) return false
  if (typeof b !== 'object' || b === null) return false
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  return keysA.every(k => Object.is(a[k], b[k]))
}

export default function createFormContext(initialState) {

  const StoreContext = createContext(null);

  function useStoreData() {
    const store = useRef(initialState);
    const subscribers = useRef(new Set());

    const get = useCallback(() => store.current, []);

    const set = useCallback((value, options = {}) => {
      const { replace = false, array = undefined, itemId = undefined, path = [] } = options;

      if (path.length > 0) {
        // Handle nested state update using path
        const existingValue = getNestedValue(store.current, path) || {};
        const newValue = replace ? value : { ...existingValue, ...value };
        store.current = setNestedValue(store.current, path, newValue);
      } else if (array !== undefined) {
        // Handle line update
        const currentLines = [...(store.current[array] || [])];
        const lineIndex = itemId;
        currentLines[lineIndex] = replace ? value : { ...currentLines[lineIndex], ...value };
        store.current = { ...store.current, [array]: currentLines };
      } else if (value != null) {
        // Handle regular state update
        store.current = replace ? value : { ...store.current, ...value };
      }

    // if (store.current.entity === 'invoice') console.log('store.current: ', store.current)

      subscribers.current.forEach((callback) => callback());
    }, []);

    const subscribe = useCallback((callback) => {
      subscribers.current.add(callback);
      return () => subscribers.current.delete(callback);
    }, []);

    return {
      get,
      set,
      subscribe,
    };
  }

  function FormStateProvider({ children }) {
    return (
      <StoreContext.Provider value={useStoreData()}>
        {children}
      </StoreContext.Provider>
    );
  }

  // Stable no-op store used when the provider hasn't mounted yet (e.g. during
  // React Fast Refresh hot reload). Keeps hooks called unconditionally so the
  // rules of hooks are never violated. The real provider mounts on the next
  // reconciliation pass and replaces this fallback.
  const nullStore = {
    get: () => initialState,
    set: () => {},
    subscribe: () => () => {},
  };

  function useStore(selector, isEqual = shallowEqual) {
    const store = useContext(StoreContext) || nullStore;

    // Keep refs to the latest selector and equality fn so the stable
    // getSnapshot closure always uses the current versions without
    // needing to be recreated on every render.
    const selectorRef = useRef(selector);
    const isEqualRef = useRef(isEqual);
    const resultRef = useRef(undefined);

    selectorRef.current = selector;
    isEqualRef.current = isEqual;

    const getSnapshot = useCallback(() => {
      const next = selectorRef.current(store.get());
      if (resultRef.current !== undefined && isEqualRef.current(resultRef.current, next)) {
        return resultRef.current; // stable reference — prevents unnecessary re-render
      }
      resultRef.current = next;
      return next;
    }, [store]);

    const state = useSyncExternalStore(
      store.subscribe,
      getSnapshot,
      () => initialState,
    );

    return [state, store.set, store.get];
  }

  return {
    FormStateProvider,
    useStore,
  };
}
